import { useState, useEffect, useCallback } from 'react';

interface QuickLocation {
  name: string;
  coordinates: [number, number];
}

interface PlaceItem {
  id?: string;
  name: string;
  title?: string;
  geo_point?: {
    lat?: number;
    latitude?: number;
    lon?: number;
    lng?: number;
    longitude?: number;
  };
}

interface RouteSegment {
  fromIndex: number;
  toIndex: number;
  distance: number;
  duration: number;
  isApproximate: boolean;
  geometry: [number, number][];
  trainRoute?: {
    geometry: [number, number][];
    distance: number;
    stations: {
      from: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
      to: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
    };
  };
}

interface FinalizedDayData {
  places: PlaceItem[];
  transportation?: Map<number, string>;
  routeSegments?: RouteSegment[];
  startLocation?: QuickLocation;
}

interface UseRouteCalculationParams {
  currentView: string;
  selectedDayIndex: number | null;
  selectedDayLocation: QuickLocation | null;
  multiDayStartLocation: QuickLocation | null;
  tripPlaces: PlaceItem[];
  dayPlaces: Map<number, PlaceItem[]>;
  combinedDayBlocks: Map<number, number[]>;
  finalizedDays: Map<number, FinalizedDayData>;
  calculateDistance: (lat1: number, lon1: number, lat2: number, lon2: number) => number;
  fetchDrivingRoute: (from: [number, number], to: [number, number], signal?: AbortSignal) => Promise<any>;
  apiBaseUrl: string;
}

/**
 * Custom hook to manage route calculation for day-to-day navigation
 * 
 * Calculates driving routes between places in a day's itinerary,
 * checks train availability for each segment, and manages route segments state.
 * 
 * @param params - Configuration parameters for route calculation
 * @returns Route segments and train availability state
 */
export function useRouteCalculation(params: UseRouteCalculationParams) {
  const {
    currentView,
    selectedDayIndex,
    selectedDayLocation,
    multiDayStartLocation,
    tripPlaces,
    dayPlaces,
    combinedDayBlocks,
    finalizedDays,
    calculateDistance,
    fetchDrivingRoute,
    apiBaseUrl,
  } = params;

  const [dayRouteSegments, setDayRouteSegments] = useState<Map<number, RouteSegment[]>>(new Map());
  const [trainAvailability, setTrainAvailability] = useState<Map<number, boolean>>(new Map());

  // Calculate the start location for the current day
  const dayStartLocation = useCallback((): QuickLocation | null => {
    if (selectedDayIndex === null) return null;

    if (selectedDayIndex === 0) {
      return multiDayStartLocation;
    } else {
      // Find the actual previous day, accounting for merged blocks
      let previousDayPlaces: PlaceItem[] | undefined;
      const previousDayIndex = selectedDayIndex - 1;
      let foundInBlock = false;

      for (const [blockStartIndex, blockIndices] of Array.from(combinedDayBlocks.entries())) {
        if (blockIndices.includes(previousDayIndex)) {
          const previousDayData = finalizedDays.get(blockStartIndex);
          previousDayPlaces = previousDayData?.places;
          foundInBlock = true;
          break;
        }
      }

      if (!foundInBlock) {
        previousDayPlaces = dayPlaces.get(previousDayIndex);
        if (!previousDayPlaces) {
          const previousDayData = finalizedDays.get(previousDayIndex);
          previousDayPlaces = previousDayData?.places;
        }
      }

      if (previousDayPlaces && previousDayPlaces.length > 0) {
        const lastPlace = previousDayPlaces[previousDayPlaces.length - 1];
        return {
          name: lastPlace.name,
          coordinates: [
            lastPlace.geo_point?.lon || lastPlace.geo_point?.lng || 0,
            lastPlace.geo_point?.lat || 0
          ]
        };
      }
    }

    return null;
  }, [selectedDayIndex, multiDayStartLocation, combinedDayBlocks, finalizedDays, dayPlaces]);

  // Calculate routes between places
  useEffect(() => {
    if (currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null || tripPlaces.length === 0) {
      return;
    }

    const startLocation = dayStartLocation();
    if (!startLocation) {
      return;
    }

    const calculateRoutes = async () => {
      // IMPORTANT: Sort places by primaryLocationIndex to ensure correct route order
      // All Location 0 places first, then Location 1, etc.
      const sortedPlaces = [...tripPlaces].sort((a, b) => {
        const indexA = (a as any).primaryLocationIndex || 0;
        const indexB = (b as any).primaryLocationIndex || 0;
        return indexA - indexB;
      });
      
      // Build itinerary: start location + sorted places
      const itinerary: Array<{ name: string; coordinates: [number, number] }> = [
        startLocation,
        ...sortedPlaces.map(place => ({
          name: place.name,
          coordinates: [
            place.geo_point?.lon || place.geo_point?.lng || 0,
            place.geo_point?.lat || 0
          ] as [number, number]
        }))
      ];

      const segments: RouteSegment[] = [];

      // Calculate route for each consecutive pair
      for (let i = 0; i < itinerary.length - 1; i++) {
        const from = itinerary[i];
        const to = itinerary[i + 1];

        // Calculate crow fly distance
        const crowFlyDistance = calculateDistance(
          from.coordinates[1], // lat
          from.coordinates[0], // lng
          to.coordinates[1],   // lat
          to.coordinates[0]    // lng
        );

        if (crowFlyDistance < 10) {
          // Use straight line for distances < 10km
          const isInDifficultTerrain = false; // Simplified for now
          const adjustedDistance = crowFlyDistance * (isInDifficultTerrain ? 1.5 : 1.2);
          const speed = isInDifficultTerrain ? 25 : 30; // km/h
          const duration = Math.round((adjustedDistance / speed) * 60); // minutes

          segments.push({
            fromIndex: i,
            toIndex: i + 1,
            distance: crowFlyDistance,
            duration,
            isApproximate: true,
            geometry: [from.coordinates, to.coordinates]
          });
        } else {
          // Fetch driving route from Mapbox for distances >= 10km
          try {
            const route = await fetchDrivingRoute(from.coordinates, to.coordinates);
            segments.push({
              fromIndex: i,
              toIndex: i + 1,
              distance: route.distance,
              duration: route.duration,
              isApproximate: false,
              geometry: route.geometry
            });
          } catch (error) {
            // Fallback to straight line
            segments.push({
              fromIndex: i,
              toIndex: i + 1,
              distance: crowFlyDistance,
              duration: Math.round((crowFlyDistance / 30) * 60),
              isApproximate: true,
              geometry: [from.coordinates, to.coordinates]
            });
          }
        }
      }

      // Store segments for this day, preserving any existing trainRoute data
      setDayRouteSegments(prev => {
        const newMap = new Map(prev);
        const existingSegments = prev.get(selectedDayIndex) || [];
        
        // Merge new segments with existing trainRoute data
        const mergedSegments = segments.map((segment, index) => {
          const existingSegment = existingSegments[index];
          if (existingSegment?.trainRoute) {
            // Preserve existing train route data
            return { ...segment, trainRoute: existingSegment.trainRoute };
          }
          return segment;
        });
        
        newMap.set(selectedDayIndex, mergedSegments);
        return newMap;
      });
    };

    calculateRoutes();
  }, [currentView, selectedDayIndex, tripPlaces, dayStartLocation, calculateDistance, fetchDrivingRoute]);

  // Check train availability for each segment
  useEffect(() => {
    if (currentView !== 'multi-day-trip-plan-day' || selectedDayIndex === null || tripPlaces.length === 0) {
      return;
    }

    const startLocation = dayStartLocation();
    if (!startLocation) {
      return;
    }

    const checkTrainAvailability = async () => {
      const availabilityMap = new Map<number, boolean>();

      // IMPORTANT: Sort places by primaryLocationIndex to match route calculation order
      const sortedPlaces = [...tripPlaces].sort((a, b) => {
        const indexA = (a as any).primaryLocationIndex || 0;
        const indexB = (b as any).primaryLocationIndex || 0;
        return indexA - indexB;
      });

      // Build itinerary with consistent format
      const itinerary = [
        {
          name: startLocation.name,
          lat: startLocation.coordinates[1],
          lng: startLocation.coordinates[0]
        },
        ...sortedPlaces.map(place => ({
          name: place.name,
          lat: place.geo_point?.lat || place.geo_point?.latitude || 0,
          lng: place.geo_point?.lon || place.geo_point?.lng || place.geo_point?.longitude || 0
        }))
      ];

      // Check each segment
      for (let i = 0; i < itinerary.length - 1; i++) {
        const from = itinerary[i];
        const to = itinerary[i + 1];

        try {
          const response = await fetch(`${apiBaseUrl}/api/advanced-trip/train-route`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              from: { lat: from.lat, lng: from.lng },
              to: { lat: to.lat, lng: to.lng }
            })
          });

          const result = await response.json();
          availabilityMap.set(i, result.available === true);
        } catch (error) {
          availabilityMap.set(i, false);
        }
      }

      setTrainAvailability(availabilityMap);
    };

    checkTrainAvailability();
  }, [currentView, selectedDayIndex, tripPlaces, dayStartLocation, apiBaseUrl]);

  return {
    dayRouteSegments,
    setDayRouteSegments,
    trainAvailability,
    setTrainAvailability,
  };
}

